import type { PortfolioItem } from "../types";
import type { DbPortfolio } from "./db";

export interface IsPortfolioDirtyParams {
  portfolio: PortfolioItem[];
  currentPortfolioId: string | null;
  portfoliosList: DbPortfolio[];
  customThemes: string[];
  isExample: boolean;
  viewMode?: 'LIST' | 'THEME' | 'PERF';
  showTechFundDetails?: boolean;
  maxRiskPerTradePct?: number;
  maxAllocationCapPct?: number;
  tradeDuration?: import('../types').TradeDuration;
}

export function checkIsPortfolioDirty({
  portfolio,
  currentPortfolioId,
  portfoliosList,
  customThemes,
  isExample,
  viewMode: _viewMode,
  showTechFundDetails: _showTechFundDetails,
  maxRiskPerTradePct,
  maxAllocationCapPct,
  tradeDuration,
}: IsPortfolioDirtyParams): boolean {
  if (!currentPortfolioId) {
    if (portfolio.length === 0) return false;
    return !isExample;
  }

  const dbPort = portfoliosList.find((p) => p.id === currentPortfolioId);
  if (!dbPort) return false;

  // Risk parameters comparison (only for non-watchlist portfolios where settings are editable)
  if (dbPort.type !== 'WATCHLIST') {
    if (maxRiskPerTradePct !== undefined) {
      const dbRisk = dbPort.maxRiskPerTradePct ?? dbPort.views?.maxRiskPerTradePct ?? 0.01;
      if (Math.abs(maxRiskPerTradePct - dbRisk) > 0.0001) return true;
    }
    if (maxAllocationCapPct !== undefined) {
      const dbCap = dbPort.maxAllocationCapPct ?? dbPort.views?.maxAllocationCapPct ?? 0.15;
      if (Math.abs(maxAllocationCapPct - dbCap) > 0.0001) return true;
    }
    if (tradeDuration !== undefined) {
      const dbDuration = dbPort.tradeDuration ?? dbPort.views?.tradeDuration ?? 'position';
      if (tradeDuration !== dbDuration) return true;
    }
  }

  // Exclude temporary tickers added solely for single-stock analysis or browsing
  const realPortfolio = portfolio.filter((item) => !item.isTemp);

  if (dbPort.items.length !== realPortfolio.length) return true;

  for (let i = 0; i < realPortfolio.length; i++) {
    const item = realPortfolio[i];
    const dbItem = dbPort.items.find((d: any) => d.id === item.id);
    if (!dbItem) return true;
    if (dbItem.ticker !== item.ticker) return true;

    const dbAlloc = parseFloat(String(dbItem.allocation).replace(/[^0-9.-]+/g, "")) || 0;
    const itemAlloc = parseFloat(String(item.allocation).replace(/[^0-9.-]+/g, "")) || 0;
    if (Math.abs(dbAlloc - itemAlloc) > 0.0001) return true;

    // Compare theme only if either has an explicitly defined non-empty theme
    const dbTheme = (dbItem.theme || "").trim().toUpperCase();
    const itemTheme = (item.theme || "").trim().toUpperCase();
    if (dbTheme !== "" || itemTheme !== "") {
      if (dbTheme !== itemTheme) return true;
    }
  }

  // List ordering comparison
  const validListOrder =
    dbPort.views?.listOrder &&
    dbPort.views.listOrder.length === dbPort.items.length &&
    dbPort.items.every((it: any) => dbPort.views!.listOrder!.includes(it.id));

  const dbListOrder = validListOrder
    ? dbPort.views!.listOrder!
    : dbPort.items.map((i: any) => i.id);

  if (dbListOrder.length !== realPortfolio.length) return true;
  for (let i = 0; i < realPortfolio.length; i++) {
    if (realPortfolio[i].id !== dbListOrder[i]) return true;
  }

  const dbCustomThemes = Array.from(new Set((dbPort.views?.customThemes || []).map((t: string) => t.trim().toUpperCase())));
  const curCustomThemes = Array.from(new Set(customThemes.map((t: string) => t.trim().toUpperCase())));

  if (dbCustomThemes.length !== curCustomThemes.length) return true;
  for (let i = 0; i < curCustomThemes.length; i++) {
    if (curCustomThemes[i] !== dbCustomThemes[i]) return true;
  }

  return false;
}
